import React, { Component } from 'react';
import cn from 'classnames';
import NavLink from 'components/Navigation/NavLink';
import AnimateHeight from 'react-animate-height';
import Caret from 'images/icons/Caret';
import { can, permActions } from 'permissions';

const localStorageKey = '__openMainNav';
const groupKeys = [
  'users',
  'subs',
  'attSubs',
  'simoSubs',
  'quickSubs',
  'device-protection',
  'billing',
  'orders',
  'archive',
  'reporting',
  'mass-actions',
];
const defaultState = {
  users: true,
  subs: true,
  attSubs: true,
  simoSubs: true,
  quickSubs: true,
  'device-protection': true,
  billing: true,
  orders: true,
  archive: false,
  reporting: true,
  'mass-actions': true,
};

const hydrateStateFromLocalStorage = () => {
  const localConfig = window.localStorage.getItem(localStorageKey);
  if (!localConfig) {
    window.localStorage.setItem(localStorageKey, JSON.stringify(defaultState));
    return defaultState;
  } else {
    return JSON.parse(localConfig);
  }
};
class MainNavLinks extends Component {
  constructor(props) {
    super(props);
    this.state = hydrateStateFromLocalStorage();
  }

  handleToggleCategory = (category) => {
    const newLocalConfig = {};
    groupKeys.forEach((key) => {
      newLocalConfig[key] = this.state[key];
    });
    newLocalConfig[category] = !this.state[category];
    this.setState({ [category]: !this.state[category] });
    window.localStorage.setItem(
      localStorageKey,
      JSON.stringify(newLocalConfig),
    );
  };

  isOpenCategory = (category) => {
    return this.state[category] ? 'auto' : 0;
  };

  generateLinks = () => {
    const { loggedInUser } = this.props;

    const homeLink = {
      to: '/',
      label: 'Home',
    };

    const usersGroup = {
      group: true,
      type: 'users',
      label: 'Users',
      links: [
        { to: '/users', label: 'All Users' },
        { to: '/users/search', label: 'Search Users' },
        {
          to: '/users/new',
          permission: can(loggedInUser, permActions.VIEW_CREATE_USER),
          label: 'New User',
        },
      ],
    };

    const subsGroup = {
      group: true,
      type: 'subs',
      label: 'Subscriptions',
      links: [
        { to: '/subs', label: 'Search All Subs' },
        {
          group: true,
          type: 'attSubs',
          permission: can(loggedInUser, permActions.VIEW_ATT_SUBS_GROUP),
          label: 'AT&T Subscriptions',
          links: [
            { to: '/att/subscriptions', label: 'All AT&T Subs' },
            { to: '/att/subscriptions/search', label: 'Search AT&T Subs' },
          ],
        },
        {
          group: true,
          type: 'simoSubs',
          permission: can(loggedInUser, permActions.VIEW_SIMO_SUBS_GROUP),
          label: 'Simo Subscriptions',
          links: [
            { to: '/simo/subscriptions', label: 'All Simo Subs' },
            { to: '/simo/subscriptions/search', label: 'Search Simo Subs' },
            { to: '/simo/devices', label: 'All Simo Devices' },
            { to: '/simo/devices/search', label: 'Search Simo Devices' },
          ],
        },
        {
          group: true,
          type: 'quickSubs',
          label: 'Quick Subscriptions',
          links: [
            {
              to: '/quick/subscriptions',
              label: 'All Quick Subs',
              permission: can(loggedInUser, permActions.VIEW_ALL_QUICK_SUBS),
            },
            {
              to: '/quick/subscriptions/search',
              label: 'Search Quick Subs',
              permission: can(loggedInUser, permActions.VIEW_SEARCH_QUICK_SUBS),
            },
            { to: '/quick/pre-activation', label: 'Quick Pre-Activation' },
            {
              to: '/quick/port-outs',
              label: 'Quick Port Outs',
              permission: can(loggedInUser, permActions.VIEW_QUICK_PORT_OUTS),
            },
          ],
        },
      ],
    };

    const deviceProtectionGroup = {
      group: true,
      permission: can(loggedInUser, permActions.VIEW_DEVICE_PROTECTION_GROUP),
      type: 'device-protection',
      label: 'Device Protection',
      links: [
        { to: '/device-protection/contracts', label: 'All Device Protection' },
      ],
    };

    const billingGroup = {
      group: true,
      type: 'billing',
      label: 'Billing',
      permission: can(loggedInUser, permActions.VIEW_BILLING_GROUP),
      links: [
        {
          to: '/purchases/payment-arrangement',
          label: 'Payment Arrangements',
          permission: can(loggedInUser, permActions.VIEW_PAYMENT_ARRANGEMENTS),
        },
        {
          to: '/rolling-arrangements',
          label: 'Rolling Arrangements',
          permission: can(loggedInUser, permActions.VIEW_ROLLING_ARRANGEMENTS),
        },
        {
          to: '/custom-plan-prices',
          label: 'Custom Plan Pricing',
          permission: can(loggedInUser, permActions.VIEW_CUSTOM_PLAN_PRICING),
        },
      ],
    };

    const ordersGroup = {
      group: true,
      permission: can(loggedInUser, permActions.VIEW_ORDERS_GROUP),
      type: 'orders',
      label: 'Orders',
      links: [
        { to: '/orders', label: 'View Orders' },
        { to: '/shippings', label: 'Shipping Estimate' },
      ],
    };

    const archiveGroup = {
      group: true,
      permission: can(loggedInUser, permActions.VIEW_ARCHIVE_GROUP),
      type: 'archive',
      label: 'Archive',
      links: [
        { to: '/subscriptions', label: 'All Sprint Subs' },
        { to: '/subscriptions/search', label: 'Search Sprint Subs' },
        { to: '/tracfone/subscriptions', label: 'All TracFone Subs' },
        { to: '/tracfone/subscriptions/search', label: 'Search TracFone Subs' },
      ],
    };

    const reportingGroup = {
      group: true,
      type: 'reporting',
      label: 'Reporting',
      permission: can(loggedInUser, permActions.VIEW_REPORTING_GROUP),
      links: [
        // { to: '/metrics', label: 'Internal Metrics' },
        { to: '/reporting/automated-reports', label: 'Automated Reports' },
      ],
    };

    const massActionsGroup = {
      group: true,
      type: 'mass-actions',
      label: 'Mass Actions',
      permission: can(loggedInUser, permActions.VIEW_MASS_ACTIONS_GROUP),
      links: [
        { to: '/mass-actions/att', label: 'AT&T' },
        { to: '/mass-actions/simo', label: 'Simo' },
        { to: '/mass-actions/quick', label: 'Quick' },
        { to: '/mass-actions/purchases', label: 'Purchases' },
      ],
    };

    const resellerLink = {
      to: '/reseller',
      label: 'Reseller',
      permission: can(loggedInUser, permActions.VIEW_RESELLER_GROUP),
    };
    const childAccountsLink = {
      to: `/users/${loggedInUser.id}/child-accounts`,
      label: 'Child Accounts',
      permission: can(loggedInUser, permActions.VIEW_RESELLER_GROUP),
    };

    const links = [
      homeLink,
      usersGroup,
      subsGroup,
      deviceProtectionGroup,
      billingGroup,
      ordersGroup,
      archiveGroup,
      reportingGroup,
      massActionsGroup,
      resellerLink,
      childAccountsLink,
    ];

    return links;
  };

  renderCaret = (category) => (
    <span
      className={cn({
        'main-nav-group-title-abs-caret': [
          'quickSubs',
          'simoSubs',
          'attSubs',
        ].includes(category),
      })}
    >
      <Caret
        className={cn({
          'main-nav-caret': true,
          'main-nav-caret-up': this.isOpenCategory(category),
        })}
      />
    </span>
  );

  renderGroup = ({ type, label, links }) => {
    return (
      <React.Fragment key={type}>
        <div
          className="main-nav-group-title mar-t-5"
          onClick={() => this.handleToggleCategory(type)}
        >
          <span>{label}</span>
          <span>{this.renderCaret(type)}</span>
        </div>
        <AnimateHeight
          duration={300}
          contentClassName="main-nav-group"
          height={this.isOpenCategory(type)}
        >
          {links.map(this.renderItem)}
        </AnimateHeight>
      </React.Fragment>
    );
  };

  renderItem = (item) => {
    if (item.permission === false) return null;

    if (item.group) return this.renderGroup(item);

    return <NavLink key={item.to} {...item} />;
  };

  render() {
    const links = this.generateLinks();

    return <div>{links.map(this.renderItem)}</div>;
  }
}

export default MainNavLinks;
